import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Modal, Select, Space, Table, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import type { FieldOption } from '../../types/reports'

type Props = {
  open: boolean
  dimensions: FieldOption[]
  onClose: () => void
  onApply: (nextDimensions: FieldOption[]) => Promise<void> | void
  isSaving?: boolean
}

type LevelRow = {
  key: string
  fieldValue: string
}

// Auto-generated level identifiers based on row position. The backend still
// keys drill metadata on drillLevel/drillNext, so we synthesise stable names
// derived purely from the order the user arranges rows in.
function levelIdForIndex(index: number) {
  return `level_${index + 1}`
}

function nextKey(existing: LevelRow[]) {
  const used = new Set(existing.map((row) => row.key))
  let i = existing.length + 1
  while (used.has(`row-${i}`)) i += 1
  return `row-${i}`
}

// Reconstruct the ordered chain from stored dimensions. We follow drillNext
// pointers starting from any root (a level nothing points to) so the same
// order the user saved comes back.
function buildInitialRows(dimensions: FieldOption[]): LevelRow[] {
  const withDrill = dimensions.filter((dimension) => (dimension.drillLevel ?? '').trim())
  if (withDrill.length === 0) return []

  const byLevel = new Map<string, FieldOption>()
  for (const dimension of withDrill) {
    const level = (dimension.drillLevel ?? '').trim()
    if (level && !byLevel.has(level)) byLevel.set(level, dimension)
  }

  const incoming = new Set(
    withDrill
      .map((dimension) => (dimension.drillNext ?? '').trim())
      .filter(Boolean),
  )

  const ordered: FieldOption[] = []
  const seenValues = new Set<string>()

  for (const dimension of withDrill) {
    const level = (dimension.drillLevel ?? '').trim()
    if (incoming.has(level)) continue
    let current: FieldOption | undefined = dimension
    while (current && !seenValues.has(current.value)) {
      seenValues.add(current.value)
      ordered.push(current)
      const nextLevel = (current.drillNext ?? '').trim()
      current = nextLevel ? byLevel.get(nextLevel) : undefined
    }
  }

  for (const dimension of withDrill) {
    if (!seenValues.has(dimension.value)) {
      seenValues.add(dimension.value)
      ordered.push(dimension)
    }
  }

  return ordered.map((dimension, index) => ({
    key: `row-${index + 1}`,
    fieldValue: dimension.value,
  }))
}

function validate(rows: LevelRow[]): string | null {
  const filled = rows.filter((row) => row.fieldValue.trim())
  if (filled.length !== rows.length) return 'Select a dimension for every level, or remove empty rows.'
  const seen = new Set<string>()
  for (const row of filled) {
    if (seen.has(row.fieldValue)) return `Dimension "${row.fieldValue}" is used in more than one level.`
    seen.add(row.fieldValue)
  }
  return null
}

export function DrilldownHierarchyModal({ open, dimensions, onClose, onApply, isSaving = false }: Props) {
  const [rows, setRows] = useState<LevelRow[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setRows(buildInitialRows(dimensions))
    setError(null)
  }, [open, dimensions])

  const availableFieldOptions = useMemo(
    () => dimensions
      .filter((dimension) => (dimension.value ?? '').trim())
      .map((dimension) => ({
        value: dimension.value,
        label: dimension.name?.trim() ? `${dimension.name} — ${dimension.value}` : dimension.value,
      })),
    [dimensions],
  )

  const usedFieldValues = useMemo(
    () => new Set(rows.map((row) => row.fieldValue).filter(Boolean)),
    [rows],
  )

  const updateRow = (key: string, updates: Partial<LevelRow>) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...updates } : row)))
  }

  const handleAddRow = () => {
    setRows((current) => [...current, { key: nextKey(current), fieldValue: '' }])
  }

  const handleDeleteRow = (key: string) => {
    setRows((current) => current.filter((row) => row.key !== key))
  }

  const moveRow = (index: number, direction: -1 | 1) => {
    setRows((current) => {
      const target = index + direction
      if (target < 0 || target >= current.length) return current
      const next = [...current]
      const [row] = next.splice(index, 1)
      if (row) next.splice(target, 0, row)
      return next
    })
  }

  const handleApply = async () => {
    const validationError = validate(rows)
    if (validationError) {
      setError(validationError)
      return
    }

    // Position becomes the level identifier — the row above is your parent,
    // the row below is your next. Dimensions dropped from the chain get their
    // drill metadata cleared so we don't leave stale pointers behind.
    const drillByFieldValue = new Map<string, { level: string; next: string | null }>()
    rows.forEach((row, index) => {
      const field = row.fieldValue.trim()
      if (!field) return
      const level = levelIdForIndex(index)
      const next = index < rows.length - 1 ? levelIdForIndex(index + 1) : null
      drillByFieldValue.set(field, { level, next })
    })

    const nextDimensions = dimensions.map((dimension) => {
      const drill = drillByFieldValue.get(dimension.value)
      if (drill) return { ...dimension, drillLevel: drill.level, drillNext: drill.next }
      if (dimension.drillLevel || dimension.drillNext) {
        return { ...dimension, drillLevel: null, drillNext: null }
      }
      return dimension
    })

    setError(null)
    await onApply(nextDimensions)
  }

  const columns = [
    {
      title: 'Level',
      dataIndex: 'level',
      width: 90,
      render: (_: unknown, __: LevelRow, index: number) => (
        <Typography.Text strong>{`Level ${index + 1}`}</Typography.Text>
      ),
    },
    {
      title: 'Dimension',
      dataIndex: 'fieldValue',
      render: (_: unknown, row: LevelRow) => {
        const options = availableFieldOptions.map((option) => ({
          ...option,
          disabled: option.value !== row.fieldValue && usedFieldValues.has(option.value),
        }))
        return (
          <Select
            className="w-full"
            value={row.fieldValue || undefined}
            options={options}
            placeholder="Select dimension"
            showSearch
            optionFilterProp="label"
            onChange={(value: string) => updateRow(row.key, { fieldValue: value })}
          />
        )
      },
    },
    {
      title: 'Order',
      dataIndex: 'order',
      width: 110,
      render: (_: unknown, _row: LevelRow, index: number) => (
        <Space size={4}>
          <Button
            type="text"
            icon={<ArrowUpOutlined />}
            size="small"
            disabled={index === 0}
            onClick={() => moveRow(index, -1)}
            aria-label="Move level up"
          />
          <Button
            type="text"
            icon={<ArrowDownOutlined />}
            size="small"
            disabled={index === rows.length - 1}
            onClick={() => moveRow(index, 1)}
            aria-label="Move level down"
          />
        </Space>
      ),
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 60,
      render: (_: unknown, row: LevelRow) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteRow(row.key)}
          aria-label="Remove level"
        />
      ),
    },
  ]

  return (
    <Modal
      title="Manage Drilldown Hierarchy"
      open={open}
      onCancel={isSaving ? undefined : onClose}
      width={720}
      okText="Apply"
      confirmLoading={isSaving}
      okButtonProps={{ disabled: isSaving }}
      cancelButtonProps={{ disabled: isSaving }}
      onOk={() => void handleApply()}
      maskClosable={!isSaving}
    >
      <Space direction="vertical" size={12} className="w-full">
        <Typography.Text type="secondary">
          Pick the dimensions to drill through and arrange them top-to-bottom. Level 1 is the outermost step; each
          row below drills into the level above it. Apply saves the hierarchy immediately.
        </Typography.Text>

        {error ? <Alert type="error" showIcon message={error} /> : null}

        {availableFieldOptions.length === 0 ? (
          <Empty description="No dimensions available. Add dimension rows on the Report Options page first." />
        ) : (
          <>
            <Table
              rowKey="key"
              size="small"
              pagination={false}
              dataSource={rows}
              columns={columns}
              locale={{ emptyText: 'No drilldown levels defined yet.' }}
            />
            <Button icon={<PlusOutlined />} onClick={handleAddRow}>
              Add level
            </Button>
          </>
        )}
      </Space>
    </Modal>
  )
}

export default DrilldownHierarchyModal
